import styled, { css } from 'styled-components'
import { media } from '../../../utils/mediaQuery'
import {
  StyledHeaderProps,
  NavSectionProps,
  NavItemProps,
} from './Header.types'

export const StyledHeader = styled.header<StyledHeaderProps>`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: ${({ theme }) => `${theme.spacing.medium} ${theme.spacing.large}`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  height: ${({ theme }) => theme.layout.appHeaderHeight};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.header};

  /* ${media.mobileOnly} {
    padding-top: env(safe-area-inset-top, 0px);
  } */

  @media print {
    display: none !important;
    visibility: hidden !important;
  }

  ${(props) =>
    props.$sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: ${props.theme.zIndex.sticky};
      box-shadow: none;
    `}

  ${(props) =>
    props.$variant === 'centered' &&
    css`
      justify-content: center;
    `}
  ${(props) =>
    props.$variant === 'minimal' &&
    css`
      justify-content: space-between;
      padding: ${({ theme }) =>
        `${theme.spacing.small} ${theme.spacing.medium}`};
      border-bottom: none;
    `}
  ${(props) =>
    props.$variant === 'default' &&
    css`
      justify-content: space-between;
    `}
`

export const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-shrink: 0;
`

export const NavSection = styled.nav<NavSectionProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xlarge};

  ${(props) =>
    props.$variant === 'default' &&
    css`
      flex: 1;
    `}
`

export const NavItemWrapper = styled.div`
  position: relative;
`

export const NavItem = styled.a<NavItemProps>`
  background: none;
  border: none;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primary
      : props.theme.colors.text.secondary};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  position: relative;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const SubmenuArrow = styled.svg`
  width: 12px;
  height: 12px;
  transition: transform 0.2s;
`

export const SubmenuContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  min-width: 160px;
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-10px')});
  transition: all 0.2s ease;
  margin-top: 4px;
`

export const SubmenuItem = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.background.tertiary : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.primary};
  font-size: 13px;
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  text-decoration: none;
  /* Ensure 44px minimum touch target */
  min-height: ${({ theme }) => theme.touchTargets.minimum};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border.focus};
  }

  svg {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${({ $active, theme }) =>
        $active ? theme.colors.background.tertiary : 'transparent'};
      color: ${({ $active, theme }) =>
        $active ? theme.colors.primary : theme.colors.text.primary};
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const SearchSection = styled.div`
  flex: 1;
  max-width: 400px;
`

export const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-shrink: 0;
`

export const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.small};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: color ${({ theme }) => theme.transitions.fast};
  /* Minimum touch target size */
  min-width: ${({ theme }) => theme.touchTargets.minimum};
  min-height: ${({ theme }) => theme.touchTargets.minimum};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
      background: none;
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const DesktopOnly = styled.div`
  display: flex;
  align-items: center;
  gap: inherit;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`

export const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const IconButton = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  .icon {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  /* Mobile: Ensure 44px minimum touch target */
  @media (max-width: 1023px) {
    min-width: ${({ theme }) => theme.touchTargets.minimum};
    min-height: ${({ theme }) => theme.touchTargets.minimum};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: none;
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

export const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  cursor: pointer;
  padding: 4px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: background 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};

  ${media.mobileOnly} {
    display: none;
  }
`

export const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: 10px;
  color: ${({ theme }) => theme.colors.primary};
`

export const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: var(--app-header-height, 48px);
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(100dvh - var(--app-header-height, 48px));
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.modal - 1};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: ${({ theme }) => theme.spacing.medium};
  /* Safe area padding for notched devices */
  padding-bottom: calc(
    ${({ theme }) => theme.spacing.large} + env(safe-area-inset-bottom, 0px)
  );
  padding-left: calc(
    ${({ theme }) => theme.spacing.medium} + env(safe-area-inset-left, 0px)
  );

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`

export const MobileNavSection = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const MobileNavItem = styled.a<NavItemProps>`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primary
      : props.theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Ensure 44px minimum touch target */
  min-height: ${({ theme }) => theme.touchTargets.minimum};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border.focus};
  }

  ${(props) =>
    props.$active &&
    css`
      background: ${props.theme.colors.background.tertiary};
    `}

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${(props) =>
        props.$active ? props.theme.colors.background.tertiary : 'transparent'};
      color: ${(props) =>
        props.$active
          ? props.theme.colors.primary
          : props.theme.colors.text.primary};
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const MobileSubmenu = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

export const MobileSubmenuItem = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.xlarge}`};
  border: none;
  text-decoration: none;
  background: ${({ $active }) =>
    $active ? 'rgba(0, 0, 0, 0.02)' : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  /* Ensure 44px minimum touch target */
  min-height: ${({ theme }) => theme.touchTargets.minimum};

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border.focus};
  }

  svg {
    width: 13px;
    height: 13px;
    margin-right: 8px;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${({ $active }) =>
        $active ? 'rgba(0, 0, 0, 0.02)' : 'transparent'};
      color: ${({ $active, theme }) =>
        $active ? theme.colors.primary : theme.colors.text.secondary};
    }

    &:active {
      background: rgba(0, 0, 0, 0.02);
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`
